import React, { useState } from 'react';
import { Box, Typography, Paper } from '@mui/material';
import type { LegacyPriceSources } from '../../../types/priceSources';
import { TOOLTIP_CARD_SX, type PriceSourceContext } from './price-source/format';
import {
  AiAnalysisSection,
  ExplanationSection,
  MarketResearchBanner,
  PriceRangeSection,
  QuoteHistorySection,
  ShoppingLinks,
  SourceSection,
} from './price-source/PriceSourceSections';

interface PriceSourceTooltipProps {
  priceSources: Pick<LegacyPriceSources, 'cost_price_details' | 'historical_data'> | null;
}

/**
 * The tooltip's sections, top to bottom, and which price sources show each:
 *
 *   source      every source; an error source ('error' source_type or
 *               fallback level) titles it "Error Information" with its details
 *   history     a quote history with records and a price - dimmed and marked
 *               "reference only" when market research was preferred
 *   range       any source with a market price range
 *   market      market research preferred over a low-confidence history
 *   links       every source (empty without search or product links)
 *   ai          a source the AI analysed (it has reasoning)
 *   explanation every source except the historical ones
 */
const SECTIONS: Array<{ id: string; shows: (ctx: PriceSourceContext) => boolean; Section: React.FC<{ ctx: PriceSourceContext }> }> = [
  { id: 'source', shows: () => true, Section: SourceSection },
  {
    id: 'history',
    shows: ({ historicalData }) => !!historicalData && historicalData.records_count > 0 && historicalData.suggested_price > 0,
    Section: QuoteHistorySection,
  },
  { id: 'range', shows: ({ details }) => !!details?.price_range, Section: PriceRangeSection },
  { id: 'market', shows: ({ serpWasPreferred }) => serpWasPreferred, Section: MarketResearchBanner },
  { id: 'links', shows: () => true, Section: ShoppingLinks },
  { id: 'ai', shows: ({ details }) => !!details?.groq_analysis?.reasoning, Section: AiAnalysisSection },
  { id: 'explanation', shows: ({ details }) => !details?.source_type?.includes('historical'), Section: ExplanationSection },
];

/**
 * Where an item's cost price came from: the source, the quote history, market
 * research and its links, the AI's analysis - or why research failed.
 */
const PriceSourceTooltip: React.FC<PriceSourceTooltipProps> = ({ priceSources }) => {
  const [showHistoricalDetails, setShowHistoricalDetails] = useState(false);

  if (!priceSources || !priceSources.cost_price_details) {
    return (
      <Paper elevation={0} sx={TOOLTIP_CARD_SX}>
        <Box sx={{ p: 1.5 }}>
          <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 500 }}>
            ⚠️ No pricing data available
          </Typography>
          <Typography variant="body2" sx={{
            color: 'text.secondary',
            fontSize: '0.85rem',
            mt: 0.5
          }}>
            Price research may have failed or is still in progress. Try refreshing the item or contact support if this persists.
          </Typography>
        </Box>
      </Paper>
    );
  }

  const details = priceSources.cost_price_details;
  const historicalData = priceSources.historical_data;

  const ctx: PriceSourceContext = {
    details,
    historicalData,
    isErrorCase: details?.source_type === 'error' || details?.fallback_level === 'error',
    // SERP was used instead of a low-confidence historical match
    serpWasPreferred: Boolean(
      historicalData &&
      historicalData.confidence === 'low' &&
      details?.source_type !== 'historical_quote' &&
      (details?.optimized_search_url || details?.analysis_metadata?.optimized_search_url || details?.primary_source)
    ),
    showHistoricalDetails,
    toggleHistoricalDetails: () => setShowHistoricalDetails(!showHistoricalDetails),
  };

  return (
    <Paper elevation={0} sx={TOOLTIP_CARD_SX}>
      <Box sx={{
        p: 1.5,
        minWidth: 280,
        maxWidth: 380,
        bgcolor: 'background.paper',
        '& > *:not(:last-child)': {
          mb: 1.5
        }
      }}>
        {SECTIONS.filter(({ shows }) => shows(ctx)).map(({ id, Section }) => (
          <Section key={id} ctx={ctx} />
        ))}
      </Box>
    </Paper>
  );
};

export default PriceSourceTooltip;
